import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import { useGroups } from '@gik/api/inkinds/groups';
import { SkeletonNetworkError, SkeletonNetworkRetryMessage } from '@gik/core/components/NetworkError/NetworkError';
import type { Group } from '@gik/core/models/gik/Group';
import { useUserStore } from '@gik/core/store/UserStore';
import { default as NetworkStore } from '@gik/core/stores/NetworkStore.ts';
import type { UIComponent } from '@gik/core/types/UI';
import bemBlock, { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Button } from '@gik/ui-legacy/Button';
import { FlexGrid } from '@gik/ui-legacy/FlexGrid';
import { FrameBone, ParagraphBone } from '@gik/ui-legacy/SkeletonLoader';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon';
import ChevronDownIcon from '@heroicons/react/outline/ChevronDownIcon';
import MailIcon from '@heroicons/react/outline/MailIcon';
import { useStore } from '@nanostores/react';
import React, { useState } from 'react';
import { InkindUserGroupCard } from './InkindUserGroupCard';

const INITIAL_VISIBLE_COUNT = 2;

export type InkindUserGroupsGridProps = UIComponent;

interface IGroupsSkeletonProps {
  skeletonItems?: number;
  onReload?: () => void;
  showRetrying?: boolean;
  showError?: boolean;
}

const skelBem = bemBlock('inkind-user-groups-skeleton');
const Skeleton = ({
  skeletonItems = 2,
  onReload,
  showRetrying,
  showError,
}: IGroupsSkeletonProps): React.ReactElement => (
  <div className="relative">
    <div className={`flex flex-col gap-4`}>
      {Array.from(Array(skeletonItems).keys()).map(index => {
        return (
          <div key={index} className={'flex gap-4'}>
            <FrameBone className={'gik-groups-frame-bone'} />
            <main>
              <ParagraphBone words={15} className={skelBem('text')} />
            </main>
          </div>
        );
      })}
    </div>
    {showRetrying && !showError && <SkeletonNetworkRetryMessage />}
    {showError && <SkeletonNetworkError onReload={onReload} />}
  </div>
);

function InkindUserGroupsGridComp({
  className,
  ...otherProps
}: React.PropsWithChildren<InkindUserGroupsGridProps>): React.ReactElement {
  const bem = useBemCN('inkind-user-groups-grid');
  const { id: userId } = useUserStore(state => ({
    id: state.id,
  }));

  const requestKey = `user-${userId}-groups`;
  const {
    data: groups,
    error,
    mutate,
  } = useGroups(
    {
      userId,
      page: 1,
      per_page: 100,
    },
    {
      requestKey,
    }
  );

  const $networkStore = useStore(NetworkStore);
  const [expanded, setExpanded] = useState(false);

  if (!groups || !NetworkStoreApi.isSuccess(requestKey)) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton
          showRetrying={NetworkStoreApi.shouldShowRetryMessage(requestKey)}
          showError={NetworkStoreApi.isError(requestKey)}
          onReload={() => {
            NetworkStoreApi.reload(requestKey);
            mutate();
          }}
        />
      </div>
    );
  }

  const hasItems = groups?.length > 0;

  if (!hasItems) {
    return (
      <FlexGrid {...bem(null, [{ empty: true }], className)} {...otherProps}>
        <p {...bem('description')}>
          Organize and manage InKind Pages for your family, employees, patients, religious community, or any group
          imaginable.
        </p>
        <div className="bg-neutral-100 text-neutral-800 p-4 rounded-2xl">
          <strong className="text-neutral-800">Using multiple Pages for one community or situation?</strong> Combine
          them into a customizable InKind Group. Supporters can browse all InKind Pages in one place, with your official
          widget on each to show it's part of your Group. &nbsp;
          <a
            href="https://www.giveinkind.com/articles/organize-support-entire-community-give-inkind-groups"
            className="gik-link font-bold"
            target="_blank"
          >
            Learn more
          </a>
        </div>
      </FlexGrid>
    );
  }

  return (
    <div {...bem(null, null, className)} {...otherProps}>
      <div className="tw-flex tw-flex-col tw-gap-4">
        {groups.map((group, index) => {
          if (!expanded && index >= INITIAL_VISIBLE_COUNT) return null;
          return <InkindUserGroupCard key={group.id} group={group} />;
        })}

        {!expanded && groups.length > INITIAL_VISIBLE_COUNT && (
          <div className="tw-text-right">
            <Button variant="secondary" size="xs" pill append={<ChevronDownIcon />} onClick={() => setExpanded(true)}>
              See All
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export const InkindUserGroupsGrid = withComponentErrorBoundary(InkindUserGroupsGridComp);
